import { cheapestWithSamePlate } from "@car/api/catalog-config";
import { Badge } from "@car/ui/components/badge";
import { Button, buttonVariants } from "@car/ui/components/button";
import { Image } from "@car/ui/components/image";
import { NativeSelect, NativeSelectOption } from "@car/ui/components/native-select";
import { Skeleton } from "@car/ui/components/skeleton";
import { cn } from "@car/ui/lib/utils";
import { Cancel01Icon, GitCompareIcon, PlusSignIcon } from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { Fragment } from "react";
import { z } from "zod";

import { openConsult } from "@/lib/consult";
import { MobileNavigation, SiteFooter } from "@/components/site-footer";
import { SiteHeader } from "@/components/site-header";
import {
  bodyLabels,
  fuelLabels,
  loadSiteContent,
  monthlyFromText,
  plateKindLabels,
  plateKindPrice,
  plateKinds,
  platePriceWords,
  specGroups,
  specLabels,
  useSiteContent,
  type SiteVehicle,
} from "@/lib/content";
import { pageCrumbs, pageMeta, pageTitle } from "@/lib/seo";
import { faPlainNumber } from "@/lib/format";
import { orpc } from "@/utils/orpc";

import { publicPageHeaders } from "./-public-cache";

const maxColumns = 3;

const compareSearchSchema = z.object({
  /** شناسه خودروها با کاما، مثل ?ids=atlas-x7,,nova-s5 (جای خالی یعنی ستونی که هنوز انتخاب نشده) */
  ids: z.string().optional().catch(undefined),
});

export const Route = createFileRoute("/compare")({
  validateSearch: compareSearchSchema,
  // already in the cache from the root route; here for the page's title and description
  loader: ({ context }) => loadSiteContent(context.queryClient),
  head: ({ loaderData }) => {
    const title = loaderData?.comparePage.title ?? "مقایسه خودروها";
    return {
      meta: [
        ...pageMeta(pageTitle(title), loaderData?.comparePage.metaDescription),
        pageCrumbs(title, "/compare"),
      ],
    };
  },
  headers: publicPageHeaders,
  component: ComparePage,
});

/** slot positions from ?ids=; an empty position is a column still waiting for a pick */
const parseIds = (raw: string | undefined) =>
  raw
    ? raw
        .split(",")
        .map((id) => id.trim())
        .slice(0, maxColumns)
    : [];

function ComparePage() {
  const { comparePage: text } = useSiteContent();
  const { ids: rawIds } = Route.useSearch();
  const navigate = Route.useNavigate();
  const positions = parseIds(rawIds);
  const ids = positions.filter(Boolean);
  const query = useQuery({
    ...orpc.site.compare.queryOptions({ input: { ids } }),
    placeholderData: (previous) => previous,
  });

  const all = query.data?.vehicles ?? [];
  const items = query.data?.items ?? [];
  const setIds = (next: string[]) =>
    navigate({
      search: { ids: next.some(Boolean) || next.length > 2 ? next.join(",") : undefined },
      replace: true,
      resetScroll: false,
    });

  // همیشه دست‌کم دو ستون انتخاب نمایش داده می‌شود تا مقایسه معنا پیدا کند.
  const slots = [...positions];
  while (slots.length < 2) slots.push("");

  return (
    <main className="min-h-[100dvh] bg-background pb-20 text-foreground md:pb-0">
      <SiteHeader />

      <section className="border-b bg-muted/45">
        <div className="mx-auto max-w-[1400px] px-4 py-10 sm:px-6 sm:py-14">
          <nav aria-label="مسیر صفحه" className="text-sm text-muted-foreground">
            <a href="/" className="hover:text-foreground">
              خانه
            </a>
            <span aria-hidden="true" className="mx-2">
              /
            </span>
            <span className="font-bold text-foreground">{text.title}</span>
          </nav>
          <h1 className="mt-4 text-3xl font-black sm:text-4xl">{text.title}</h1>
          <p className="mt-3 max-w-[60ch] leading-7 text-muted-foreground">{text.lede}</p>
        </div>
      </section>

      <section className="py-8 sm:py-12">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6">
          {query.isPending ? (
            <div className="grid gap-4">
              <Skeleton className="h-14 rounded-xl" />
              <Skeleton className="h-96 rounded-2xl" />
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-end gap-3">
                {slots.map((id, index) => (
                  <div key={index} className="grid gap-1.5">
                    <label
                      htmlFor={`compare-slot-${index}`}
                      className="text-xs font-bold text-muted-foreground"
                    >
                      خودرو {faPlainNumber(index + 1)}
                    </label>
                    <div className="flex items-center gap-1.5">
                      <NativeSelect
                        id={`compare-slot-${index}`}
                        className="min-w-52"
                        value={id}
                        onChange={(event) => {
                          const next = [...slots];
                          next[index] = event.target.value;
                          setIds(next);
                        }}
                      >
                        <NativeSelectOption value="">انتخاب خودرو…</NativeSelectOption>
                        {all
                          .filter((vehicle) => vehicle.id === id || !slots.includes(vehicle.id))
                          .map((vehicle) => (
                            <NativeSelectOption key={vehicle.id} value={vehicle.id}>
                              {vehicle.name} {vehicle.trim}
                            </NativeSelectOption>
                          ))}
                      </NativeSelect>
                      {slots.length > 2 ? (
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={`حذف ستون ${faPlainNumber(index + 1)}`}
                          onClick={() => setIds(slots.filter((_, slot) => slot !== index))}
                        >
                          <HugeiconsIcon icon={Cancel01Icon} strokeWidth={1.8} />
                        </Button>
                      ) : null}
                    </div>
                  </div>
                ))}
                {slots.length < maxColumns ? (
                  <Button variant="outline" onClick={() => setIds([...slots, ""])}>
                    <HugeiconsIcon icon={PlusSignIcon} strokeWidth={1.8} />
                    افزودن خودرو
                  </Button>
                ) : null}
              </div>

              {items.length < 2 ? (
                <p className="mt-8 rounded-2xl border border-dashed p-8 text-center text-muted-foreground">
                  برای شروع، دست‌کم دو خودرو را از فهرست بالا انتخاب کنید.
                </p>
              ) : (
                <CompareTable items={items} />
              )}

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <Link to="/vehicles" className={buttonVariants({ variant: "outline" })}>
                  دیدن همه خودروها
                </Link>
                <button
                  type="button"
                  aria-haspopup="dialog"
                  onClick={openConsult}
                  className={buttonVariants({ variant: "ghost" })}
                >
                  {text.consultCta}
                </button>
              </div>
            </>
          )}
        </div>
      </section>

      <SiteFooter />
      <MobileNavigation />
    </main>
  );
}

/**
 * ستون ویژگی‌ها با اسکرول افقی جدول (موبایل) در لبه‌ی شروع می‌ماند؛ پس‌زمینه‌ی مات تا ستون‌های
 * زیرش دیده نشوند و خط لبه با سایه (border در جدول collapse همراه سلول چسبان نمی‌آید)
 */
const stickyColumn =
  "sticky start-0 z-10 px-3 text-start text-muted-foreground shadow-[inset_-1px_0_0_var(--border)] sm:px-5 rtl:shadow-[inset_1px_0_0_var(--border)]";

function CompareTable({ items }: { items: SiteVehicle[] }) {
  // like for like: the price of a plate every compared model has a published price with
  const cheapest = cheapestWithSamePlate(items);

  // one row per plate kind any compared model is sold with; «—» where a model is not,
  // «استعلام قیمت» where its price is on request
  const priceRows = plateKinds
    .filter((kind) => items.some((item) => plateKindPrice(item, kind) !== null))
    .map((kind) => ({
      label: `قیمت نقدی با ${plateKindLabels[kind]}`,
      values: items.map((item) => platePriceWords(plateKindPrice(item, kind))),
      strong: true,
    }));

  const baseRows: { label: string; values: string[]; strong?: boolean }[] = [
    ...priceRows,
    {
      label: "اقساط ماهانه از",
      values: items.map(monthlyFromText),
    },
    {
      label: "سال / تیپ",
      values: items.map((item) => `${faPlainNumber(item.year)} ${item.trim}`),
    },
    { label: "نوع بدنه", values: items.map((item) => bodyLabels[item.body]) },
    { label: "نوع سوخت", values: items.map((item) => fuelLabels[item.fuel]) },
    { label: "گیربکس", values: items.map((item) => item.transmission) },
    {
      label: "زمان تحویل",
      values: items.map((item) => `${faPlainNumber(item.deliveryDays)} روز کاری`),
    },
    { label: "گارانتی", values: items.map((item) => item.warranty) },
    { label: "شعبه موجودی", values: items.map((item) => item.branchName || "—") },
  ];

  return (
    <>
      {/* روی گوشی جدول از عرض صفحه بلندتر است؛ بی این نشانه معلوم نیست خودروی دوم کنار است */}
      {items.length > 1 ? (
        <p className="mt-6 text-xs text-muted-foreground sm:hidden">
          برای دیدن خودروهای دیگر، جدول را به کنار بکشید.
        </p>
      ) : null}
      <div className="mt-2 overflow-x-auto rounded-2xl border bg-card sm:mt-6">
        <table className="w-full min-w-[336px] text-sm sm:min-w-[720px]">
          <thead>
            <tr className="border-b bg-muted/45">
              <th
                scope="col"
                className={cn(
                  stickyColumn,
                  "w-20 bg-[color-mix(in_oklab,var(--muted)_45%,var(--card))] px-2 py-4 text-xs font-bold sm:w-52 sm:px-5 sm:text-sm",
                )}
              >
                ویژگی
              </th>
              {items.map((item) => (
                <th key={item.id} scope="col" className="px-2 py-4 text-start align-top sm:px-5">
                  <Link
                    to="/vehicles/$vehicleId"
                    params={{ vehicleId: item.id }}
                    className="grid gap-2"
                  >
                    <Image
                      src={item.image}
                      alt={item.name}
                      width={176}
                      height={96}
                      className="h-20 w-full max-w-28 rounded-xl object-cover sm:h-24 sm:max-w-44"
                    />
                    <span className="font-black hover:underline">{item.name}</span>
                  </Link>
                  {cheapest?.ids.includes(item.id) ? (
                    <Badge
                      variant="secondary"
                      className="mt-2 h-auto whitespace-normal py-1 text-start sm:h-5 sm:whitespace-nowrap sm:py-0.5"
                    >
                      کم‌هزینه‌ترین با {plateKindLabels[cheapest.kind]}
                    </Badge>
                  ) : null}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {baseRows.map((row) => (
              <tr key={row.label} className="border-b">
                <th
                  scope="row"
                  className={cn(
                    stickyColumn,
                    "bg-card px-2 py-3.5 text-xs font-normal sm:px-5 sm:text-sm",
                  )}
                >
                  {row.label}
                </th>
                {row.values.map((value, index) => (
                  <td
                    key={index}
                    className={cn("px-2 py-3.5 sm:px-5", row.strong ? "font-bold" : undefined)}
                  >
                    {value}
                  </td>
                ))}
              </tr>
            ))}
            {specGroups.map((group) => (
              <Fragment key={group.title}>
                <tr className="border-b bg-muted/45">
                  <th
                    scope="colgroup"
                    colSpan={items.length + 1}
                    className="px-5 py-2.5 text-start font-black"
                  >
                    {/* عنوان گروه هم با اسکرول افقی کنار ستون ویژگی می‌ماند */}
                    <span className="sticky start-5">{group.title}</span>
                  </th>
                </tr>
                {group.keys.map((key) => (
                  <tr key={key} className="border-b last:border-b-0">
                    <th
                      scope="row"
                      className={cn(
                        stickyColumn,
                        "bg-card px-2 py-3.5 text-xs font-normal sm:px-5 sm:text-sm",
                      )}
                    >
                      {specLabels[key]}
                    </th>
                    {items.map((item) => (
                      <td key={item.id} className="px-2 py-3.5 sm:px-5">
                        {item.specs[key]}
                      </td>
                    ))}
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
        <div className="flex flex-wrap gap-3 border-t p-4">
          {items.map((item) => (
            <Link
              key={item.id}
              to="/vehicles/$vehicleId"
              params={{ vehicleId: item.id }}
              className={cn(buttonVariants({ variant: "outline", size: "sm" }), "rounded-xl")}
            >
              <HugeiconsIcon icon={GitCompareIcon} strokeWidth={1.8} />
              جزئیات {item.name}
            </Link>
          ))}
        </div>
      </div>
    </>
  );
}
